/* Do not pass null as the property for data - it is not handled correctly by AWS Lambda on non GET requests.
   It will work locally but fail on AWS - pass an empty object instead */

import { useMutation } from "@tanstack/react-query";

import { apiCall, apiCallWithFormData } from "./shared";

export function useCreateWriter() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/post",
    path: "/api/v1/writer/create",
    method: "post",
    data,
  }));
}

export function useUpdateWriter() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/put",
    path: "/api/v1/writer/update",
    method: "put",
    data,
  }));
}

export function useUpdateMyProfile() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/put",
    path: "/api/v1/user/setprofile",
    method: "put",
    data,
  }));
}

export function useCreateSong() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/post",
    path: "/api/v1/work/create",
    method: "post",
    data,
  }));
}

export function useCreateRecording() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/post",
    path: "/api/v1/recording/create",
    method: "post",
    data,
  }));
}

export function useUpdateSong() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/put",
    path: "/api/v1/work/update",
    method: "put",
    data,
  }));
}

export function useDeleteSong() {
  return useMutation(data =>
    apiCall({
      path: `/api/v1/work/deletework`,
      endpoint: "/rightsapp/delete",
      method: "delete",
      data,
    }),
  );
}

export function useDeleteRecording() {
  return useMutation(data =>
    apiCall({
      path: `/api/v1/recording/deleterecording`,
      endpoint: "/rightsapp/delete",
      method: "delete",
      data,
    }),
  );
}

export function useSignup() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/post",
    path: "/api/v1/user/signup",
    method: "post",
    data,
    clientId: true,
    noAuth: true,
    skipKycCheck: true,
  }));
}

export function useSignupConfirm() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/post",
    path: "/api/v1/user/verifyuser",
    method: "post",
    data,
    clientId: true,
    noAuth: true,
    skipKycCheck: true,
  }));
}

export function useSaveUserMeta() {
  return useMutation(data => apiCall({
    endpoint: "/usermeta",
    method: "post",
    data,
    skipKycCheck: true,
  }));
}

export function usePostOpportunity() {
  return useMutation(data => apiCall({
    endpoint: "/opportunity",
    method: "post",
    data,
  }));
}

export function usePostSync() {
  return useMutation(data => apiCall({
    endpoint: "/opportunity",
    method: "post",
    data,
  }));
}

export function usePostBroadcast() {
  return useMutation(data => apiCall({
    endpoint: "/broadcast",
    method: "post",
    data,
  }));
}

export function usePostGigClaim() {
  return useMutation(data => apiCall({
    endpoint: "/gig-claim",
    method: "post",
    data,
  }));
}

export function useUpdateGigClaim() {
  return useMutation(data => apiCall({
    endpoint: "/gigs/update",
    method: "put",
    data,
  }));
}

export function useDeleteGigClaim() {
  return useMutation(data => apiCall({
    endpoint: `/gigsClaim/delete?id=${data.id}&account_code=${data.account_code}`,
    method: "delete",
  }));
}

export function usePutBroadcast() {
  return useMutation(data => apiCall({
    endpoint: "/broadcast",
    method: "put",
    data,
  }));
}

export function usePostSetlist() {
  return useMutation(data => apiCall({
    endpoint: "/setlist",
    method: "post",
    data,
  }));
}

export function usePutSetlist() {
  return useMutation(data => apiCall({
    endpoint: "/setlist",
    method: "put",
    data,
  }));
}

export function useAddArtistAccount() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/post",
    path: "/api/v1/artistaccount/create",
    method: "post",
    data,
  }));
}

export function useUpdateArtistAccount() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/put",
    path: "/api/v1/artistaccount/update",
    method: "put",
    data,
  }));
}

export function useUpdateArtistAccountMeta() {
  return useMutation(data => apiCall({
    endpoint: "/artist-meta",
    method: "post",
    data,
  }));
}

export function useResetPassword() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/put",
    path: "/api/v1/user/forgotpassword",
    method: "put",
    data,
    clientId: true,
    noAuth: true,
    skipKycCheck: true,
  }));
}

export function useResetPasswordConfirm() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/put",
    path: "/api/v1/user/confirmresetpassword",
    method: "put",
    data,
    clientId: true,
    noAuth: true,
    skipKycCheck: true,
  }));
}

export const useS3Put = () => useMutation(({ key, contenttype }) => apiCall({ endpoint: `/s3/put?key=${key}&contenttype=${contenttype}`, method: "get" }));

export function useUpdateRecordingMeta() {
  return useMutation(data => apiCall({
    endpoint: "/recording-meta",
    method: "post",
    data,
  }));
}

export function useUpdateSuggestion() {
  return useMutation(data => apiCall({
    endpoint: "/spotify-suggestion",
    method: "post",
    data,
  }));
}

export function useElasticQuery() {
  return useMutation(data => apiCall({
    endpoint: "/royalties/get",
    method: "post",
    data,
    skipKycCheck: true,
  }));
}

export function useAttachAmendmentOnlyMp3Put() {
  return useMutation(data => apiCallWithFormData({
    endpoint: "/rightsapp/putRecordingWithBinary",
    path: "/api/v1/recording/attachamendmentonlymp3",
    method: "put",
    data,
  }));
}

export function useAttachMp3Put() {
  return useMutation(data => apiCallWithFormData({
    endpoint: "/rightsapp/putRecordingWithBinary",
    path: "/api/v1/recording/attachmp3",
    method: "put",
    data,
  }));
}

export function useAttachAmendmentOnlyMp3ByPreSignedUrlPost() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/post",
    path: "/api/v1/recording/attachamendmentonlymp3bypresignedurl",
    method: "post",
    data,
  }));
}

export function useAttachMp3ByPresignedUrlPost() {
  return useMutation(data => apiCall({
    endpoint: "/rightsapp/post",
    path: "/api/v1/recording/attachmp3bypresignedurl",
    method: "post",
    data,
  }));
}

export function useGetMyAccountMutation() {
  return useMutation(artistaccountcode => apiCall({
    endpoint: "/rightsapp/get",
    path: `/api/v1/artistaccount/useGetMyAccount?artistaccountcode=${artistaccountcode}`,
  }));
}

export function useRefreshKycStatus() {
  return useMutation(() => apiCall({
    endpoint: "/rightsapp/post",
    path: "/api/v1/user/refreshkyc",
    method: "post",
    data: {},
    skipKycCheck: true,
  }));
}

export function useStartKyc() {
  return useMutation(() => apiCall({
    endpoint: "/rightsapp/post",
    path: "/api/v1/user/startkyc",
    method: "post",
    data: {},
    skipKycCheck: true,
  }));
}
